import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    effect,
    EventEmitter,
    inject,
    input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { SelectData } from '@shared/models/select-input.model';
import { Subject, takeUntil } from 'rxjs';
import { MatFormField, MatLabel, MatSuffix, MatHint } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { NgClass } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'z-select',
    templateUrl: './z-select.component.html',
    styleUrls: ['./z-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatLabel, MatSelect, ReactiveFormsModule, MatCheckbox, NgClass, MatOption, MatIconButton, MatSuffix, MatIcon, MatHint, TranslatePipe]
})
export class ZSelectComponent implements OnInit, OnDestroy {
    readonly FILTER_INPUT_SHOW_CONDITION_SIZE = 10;

    readonly label = input<string>('');
    readonly multiple = input<boolean>(false);
    readonly formControlRef = input<FormControl>(new FormControl());
    readonly items = input<SelectData[]>([]);
    readonly required = input<boolean>(false);
    readonly hint = input<string>('');
    readonly errorMessage = input<string>('');
    readonly id = input<string>('');

    private readonly cdr = inject(ChangeDetectorRef);

    @Output()
    selectionChange: EventEmitter<SelectData[]> = new EventEmitter();

    selectItems: SelectData[] = [];
    filteredItems: SelectData[] = [];

    appearance: MatFormFieldAppearance = 'outline';

    filterControl: FormControl<string | null> = new FormControl('');

    private destroySubject: Subject<boolean> = new Subject();

    constructor() {
        effect(() => {
            // Safely handle arrays if signals return null/undefined initially
            const items = this.items() || [];
            this.selectItems = items;
            this.filteredItems = items;
            this.cdr.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.destroySubject.next(true);
        this.destroySubject.complete();
    }

    ngOnInit(): void {
        const control = this.formControlRef();
        if (control) {
            control.valueChanges.pipe(takeUntil(this.destroySubject))
                .subscribe(() => {
                    this.filterControl.reset();
                    this.cdr.markForCheck();
                });
        }

        this.filterControl.valueChanges
            .pipe(takeUntil(this.destroySubject))
            .subscribe((value: any) => {
                const val = value as string | null;
                if (!val) {
                    this.filteredItems = this.selectItems;
                    return;
                }
                const normalizedFilter = this.removeAccents(
                    val.trim().toLocaleLowerCase()
                );

                const filteredValues = this.selectItems.filter((item: SelectData) => {
                    if (!item.label) return false;
                    const filterWithoutAccents = this.removeAccents(
                        item.label.toLocaleLowerCase()
                    );
                    return filterWithoutAccents.includes(normalizedFilter);
                });
                if (
                    JSON.stringify(this.filteredItems) !== JSON.stringify(filteredValues)
                ) {
                    this.filteredItems = filteredValues;
                }
            });
    }

    private removeAccents(text: string): string {
        return text ? text.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
    }

    toogleAll(): void {
        const control = this.formControlRef();
        if (!control) return;
        control.setValue(
            control.value?.length === this.filteredItems.length
                ? []
                : this.filteredItems
        );
    }

    compareObjects(object1: any, object2: any): boolean {
        if (object1?.value !== undefined && object2?.value !== undefined) {
            return object1.value === object2.value;
        }
        return object1 === object2;
    }
}
